<template lang="pug">
.d-flex.align-center
    span.dd-status-dot(:style="{ backgroundColor: dotColor }")
    .d-flex.flex-column.dd-status-text(v-if="!cancelInfo && !isMultiCutoff")
        .dd-status-line
            span {{ $t(`enums.order_status.${status}`) }}
            status-badge(v-if="isAps" :tone="apsTone" :href="apsLink" target="_blank") {{ apsText }}
        small.d-flex.flex-nowrap.g-73--text(v-if="updatedAt") {{updatedAt | transformDate}}
    v-tooltip(v-else, bottom)
        template(v-slot:activator="{ on }")
            .d-flex.flex-column.dd-status-text(v-on="on")
                .dd-status-line
                    span {{ $t(`enums.order_status.${status}`) }}{{ isMultiCutoff ? ` ${multicutoffProgressText}` : '' }}
                    status-badge(v-if="cancelInfo" :tone="cancelTone") {{ cancelLabel }}
                    status-badge(v-if="isAps" :tone="apsTone" :href="apsLink" target="_blank") {{ apsText }}
                small.d-flex.flex-nowrap.g-73--text(v-if="updatedAt") {{updatedAt | transformDate}}
        span.d-block(v-if="cancelTooltip" :class="{ 'mb-2': isMultiCutoff }") {{ cancelTooltip }}
        span(v-if="isMultiCutoff")
            span.d-block(v-for="detail in multicutoffDetails" :key="detail.line1")
                span.d-block {{ detail.line1 }}
                span.d-block.mb-2 {{ detail.line2 }}
</template>

<script>
import Config from '@/config.loader'
import { multicutoffDetails } from '@/services/Orders/CanteenMultiCutoffRuleLogic'
import { classifyCancelReason } from '@/services/Orders/cancelReason'
import StatusBadge from '@/components/elements/StatusBadge.vue'

export default {
    components: {
        StatusBadge,
    },
    props: {
        item: null,
    },
    data: () => ({
        apsBase: Config.getConfigValue('VUE_APP_APS_BASE'),
    }),
    computed: {
        status() {
            return this.item?.status
        },
        updatedAt() {
            return this.item?.updatedAt
        },
        cancelInfo() {
            return classifyCancelReason(this.item)
        },
        cancelLabel() {
            const info = this.cancelInfo
            if (!info) {
                return null
            }
            if (info.group === 'deliverect' || info.group === 'other') {
                return this.$t(`enums.order_cancel_display.${info.group}`)
            }
            return this.$t(
                `enums.order_cancel_display.${info.group}.${info.key}`
            )
        },
        cancelTone() {
            return this.cancelInfo?.group === 'payment' ? 'critical' : 'neutral'
        },
        cancelTooltip() {
            const info = this.cancelInfo
            if (!info) {
                return null
            }
            switch (info.group) {
                case 'customer':
                    return this.$t(
                        `labels.order_cancel_tooltip.customer_${
                            info.channel || 'unknown'
                        }`
                    )
                case 'payment': {
                    const head = info.method
                        ? this.$t(
                              'labels.order_cancel_tooltip.payment_failed_method',
                              { method: info.method }
                          )
                        : this.$t('labels.order_cancel_tooltip.payment_failed')
                    return info.detail ? `${head}: ${info.detail}` : head
                }
                case 'admin': {
                    const type = this.$t(
                        `enums.order_cancel_reasons.${info.key}`
                    )
                    return info.detail ? `${type}: ${info.detail}` : type
                }
                default:
                    return info.detail
            }
        },
        // order-status dot colours: new teal (#00666d, DoorDash Teams; user decision 2026-11-03), confirmed green (the merchant portal's status dot
        // #1fa74f, the finished delivery's too; user
        // decision 2026-10-30), cancelled red, awaiting payment amber, anything else burgundy
        dotColor() {
            switch (this.status) {
                case 'approved':
                    return '#1fa74f'
                case 'init-payment':
                    return '#d5a600'
                case 'new':
                    return '#00666d'
                case 'cancelled':
                case 'cancelled-after-approval':
                    return '#b71000'
                default:
                    return '#7f1041'
            }
        },
        isMultiCutoff() {
            return (
                (this.item?.cloudcanteen || this.item?.residentialcanteen) &&
                !this.item?.userProfile &&
                this.item?.canteenRules?.length > 1
            )
        },
        multicutoffProgressText() {
            const result = {
                confirmed: [],
                total: this.item?.canteenRules?.length || 0,
            }

            if (this.item?.canteenRules?.length > 1) {
                const now = this.$date()
                let j = 0

                for (let i in this.item.canteenRules) {
                    const rule = this.item.canteenRules[i]
                    const cutoff = this.$date(rule.cutoffTime)

                    const notification = (
                        this.item.canteenData?.collected_orders_notification ||
                        []
                    ).find((el) => cutoff.isSame(el.cutoffTime, 'm'))
                    const subOrders = (
                        this.item.canteenSubOrdersByCutoff || []
                    ).find((el) => cutoff.isSame(el.cutoffTime, 'm'))

                    if (now.isAfter(cutoff)) {
                        if (!subOrders) {
                            result.total--
                        } else if (notification) {
                            result.confirmed.push(++j)
                        }
                    }
                }

                result.confirmed = result.confirmed.join(', ') || '0'

                return `(${result.confirmed} / ${result.total})`
            }

            return ''
        },
        multicutoffDetails() {
            const details = multicutoffDetails(this.item) || []
            return details.map((el) => {
                const notiTxt = el.confirmed
                    ? this.$t('orders.confirmed') + ' | '
                    : ''
                return {
                    line1: `${el.nr}. ${this.$t('orders.multi_cutoff')}, ${
                        el.cutoff
                    }`,
                    line2:
                        notiTxt +
                        this.$tc('labels.orders_count', el.count, {
                            n: el.count,
                        }),
                }
            })
        },
        isAps() {
            return this.item?.restaurantData?.job_id
        },
        apsTone() {
            switch (this.item?.restaurantData?.status) {
                case 'working':
                    return 'info'
                case 'done':
                    return 'success'
                case 'halted':
                case 'error':
                    return 'critical'
                default:
                    return 'attention'
            }
        },
        apsText() {
            const data = this.item?.restaurantData?.data
            const status = this.item?.restaurantData?.status

            if (
                data &&
                (status === 'working' ||
                    status === 'error' ||
                    status === 'halted') &&
                data.channel_step_id
            ) {
                const steps = (data?.channel_steps || []).filter(
                    (step) => step.is_enabled
                )
                const currentStepCount =
                    steps.findIndex(
                        (step) => step.id == data?.channel_step_id
                    ) + 1

                return `${currentStepCount} / ${steps.length}`
            } else if (status === 'done') {
                return 'Done'
            } else {
                return 'Pending'
            }
        },
        apsLink() {
            return this.item?.restaurantData?.job_id
                ? `${this.apsBase}/jobs/${this.item?.restaurantData?.job_id}`
                : ''
        },
    },
}
</script>

<style lang="scss">
.editable-field {
    width: 100px;
}

// the status and its badges ("Late cancel", "Admin · Other") wrap in a narrow column instead of
// widening it (sweep 2026-11-04)
.dd-status-text {
    min-width: 0;
}

.dd-status-line {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
}
</style>
